import React, { useEffect, useState } from "react";
import AdminAppBar from "../../adminComponents/AdminAppBar";
import { toast, ToastContainer } from "react-toastify";
import BackArrows from "../../Form/BackArrow/BackArrows";
import { showErrorToast, showSuccessToast } from "../../Constant/Notification";
import AmenitiesService from "../../Services/AmenitiesService";
import { useNavigate } from "react-router-dom";

const PropertyInventory = () => {
  const navigator = useNavigate();
  const [loading, setLoading] = useState(false);
  const [amenities, setAmenities] = useState({
    basic: [],
    feature: [],
    nearby: [],
  });
  const [limits, setLimits] = useState({
    basic: 250,
    feature: 250,
    nearby: 250,
  });

  const fetchAmenities = async () => {
    try {
      const response = await AmenitiesService.getAmentities();
      const data = response.data || [];
      setAmenities({
        basic: data.filter((item) => item.type === 1),
        feature: data.filter((item) => item.type === 2),
        nearby: data.filter((item) => item.type === 3),
      });
    } catch (error) {
    //  showErrorToast(error.response?.data || "Failed to fetch amenities");
    console.log(error)
    }
  };

  useEffect(() => {
    fetchAmenities();
  }, []);

  const handleBack = () => {
    navigator(-1);
  };

  return (
    <div className="bg-appGrayBackground min-h-screen">
      <AdminAppBar />
      <ToastContainer />
      <div className="pt-4 px-6 pb-12">
        <div className="mt-20 pt-8 px-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-6">
              <BackArrows onClick={handleBack} />
              <h1 className="text-[32px] font-medium">Property Inventory</h1>
            </div>
          </div>
        </div>
        <div className="mt-5 mx-8 bg-white rounded-md shadow-md p-6">
          <AmenitiesSection
            title="Basic Amenities"
            amenities={amenities.basic}
            type={1}
            limit={limits.basic}
            fetchAmenities={fetchAmenities}
            setAmenities={(updated) =>
              setAmenities((prev) => ({ ...prev, basic: updated }))
            }
            note="Add essential amenities available within the property, e.g., Wi-Fi, Air Conditioning, Kitchen, Parking."
          />
          <AmenitiesSection
            title="Feature Amenities"
            amenities={amenities.feature}
            type={2}
            limit={limits.feature}
            fetchAmenities={fetchAmenities}
            setAmenities={(updated) =>
              setAmenities((prev) => ({ ...prev, feature: updated }))
            }
            note="Add special features that enhance the property, e.g., Swimming Pool, Gym, Balcony, Smart Home System."
          />
          <AmenitiesSection
            title="Nearby Amenities"
            amenities={amenities.nearby}
            type={3}
            limit={limits.nearby}
            fetchAmenities={fetchAmenities}
            setAmenities={(updated) =>
              setAmenities((prev) => ({ ...prev, nearby: updated }))
            }
            note="Add amenities located near the property, e.g., Supermarket, Public Transport, Park, Hospital."
          />
        </div>
      </div>
    </div>
  );
};

const AmenitiesSection = ({
  title,
  amenities,
  type,
  limit,
  note,
  setAmenities,
  fetchAmenities,
}) => {
  const [inputValue, setInputValue] = useState("");
  const [editingAmenity, setEditingAmenity] = useState(null);
  const [isLoading, setIsLoading] = useState(false);

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };

  const handleAddOrUpdate = async () => {
    const trimmedValue = inputValue.trim();
    if (!trimmedValue) {
      showErrorToast("Amenity name cannot be empty");
      return;
    }

    if (trimmedValue.length > 50) {
      showErrorToast("Amenity name cannot exceed 50 characters");
      return;
    }

    const isDuplicate = amenities.some(
      (item) =>
        item.name.toLowerCase() === trimmedValue.toLowerCase() &&
        item.id !== editingAmenity?.id
    );
    if (isDuplicate) {
      showErrorToast("This amenity already exists");
      return;
    }
    setIsLoading(true);
    try {
      if (editingAmenity) {
        const data = {
          id: editingAmenity.id,
          name: trimmedValue,
          type,
        };
        await AmenitiesService.updateAmenities(data);
        setAmenities(
          amenities.map((item) =>
            item.id === editingAmenity.id
              ? { ...item, name: trimmedValue }
              : item
          )
        );
        showSuccessToast("Amenity updated successfully");
      } else {
        if (amenities.length >= limit) {
          showErrorToast(`Maximum limit of ${limit} amenities reached`);
          return;
        }
        const response = await AmenitiesService.createAmenities({
          name: trimmedValue,
          type,
        });
        setAmenities([...amenities, response.data]);
        showSuccessToast("Amenity created successfully");
      }
      setInputValue("");
      setEditingAmenity(null);
      fetchAmenities();
    } catch (error) {
      showErrorToast(error.response?.data || "Operation failed");
    } finally {
      setIsLoading(false);
    }
  };

  const handleEdit = (amenity) => {
    setEditingAmenity(amenity);
    setInputValue(amenity.name);
  };

  const handleCancel = () => {
    setInputValue("");
    setEditingAmenity(null);
  };

  return (
    <div className="mb-8">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-bold">{title}</h1>
        <span
          className={`text-sm ${
            amenities.length >= limit * 0.8 ? "text-red-500" : "text-gray-500"
          }`}
        >
          {amenities.length}/{limit} {title} used
        </span>
      </div>
      <div className="flex gap-16 mt-4">
        <div className="custom-scrollbar overflow-x-hidden flex flex-wrap flex-auto w-7/12 h-64 rounded-md p-3 gap-x-2 gap-y-2 bg-appGrayBackground overflow-y-auto">
          {amenities.map((item) => (
            <div
              key={item.id}
              className="bg-customGreen h-8 rounded-lg px-3 text-sm font-normal text-white mr-2 mb-2 flex items-center"
            >
              <span>{item.name}</span>
              <button
                className="ml-2 focus:outline-none"
                onClick={() => handleEdit(item)}
                disabled={amenities.length >= limit && !editingAmenity}
              >
                <svg
                  width="15"
                  height="15"
                  viewBox="0 0 26 26"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M3.08333 22.9167H5.10208L18.95 9.06875L16.9312 7.05L3.08333 20.8979V22.9167ZM0.25 25.75V19.7292L18.95 1.06458C19.2333 0.804861 19.5462 0.604167 19.8885 0.4625C20.2309 0.320833 20.591 0.25 20.9688 0.25C21.3465 0.25 21.7125 0.320833 22.0667 0.4625C22.4208 0.604167 22.7278 0.816667 22.9875 1.1L24.9354 3.08333C25.2188 3.34306 25.4253 3.65 25.5552 4.00417C25.6851 4.35833 25.75 4.7125 25.75 5.06667C25.75 5.44444 25.6851 5.80451 25.5552 6.14687C25.4253 6.48924 25.2188 6.80208 24.9354 7.08542L6.27083 25.75H0.25ZM17.9229 8.07708L16.9312 7.05L18.95 9.06875L17.9229 8.07708Z"
                    fill="#fff"
                  />
                </svg>
              </button>
            </div>
          ))}
        </div>
        <div className="flex flex-col w-5/12">
          <div className="flex gap-4 items-start">
            <div className="flex flex-col gap-1 max-w-md w-full">
              <input
                type="text"
                value={inputValue}
                onChange={handleInputChange}
                maxLength={50}
                placeholder={`Enter ${title.toLowerCase()}`}
                className="px-4 py-2 h-10 text-sm rounded-md border border-gray-300 input-focus"
                disabled={amenities.length >= limit && !editingAmenity}
              />
              <div className="text-sm text-gray-500">{note}</div>
            </div>
            <div className="flex gap-2">
              <button
                onClick={handleAddOrUpdate}
                disabled={
                  (amenities.length >= limit && !editingAmenity) || isLoading
                }
                className={`h-10 px-4 text-sm font-medium rounded-md transition duration-200 ${
                  isLoading
                    ? "text-gray-400 bg-gray-200 cursor-not-allowed"
                    : amenities.length >= limit && !editingAmenity
                    ? "text-red-400 bg-red-100 cursor-not-allowed"
                    : "text-white bg-customGreen"
                }`}
              >
                {isLoading ? "Loading..." : editingAmenity ? "Update" : "Add"}
              </button>

              {editingAmenity && (
                <button
                  onClick={handleCancel}
                  className="h-10 px-4 text-sm font-medium rounded-md transition duration-200 text-white bg-gray-500 hover:bg-gray-600"
                >
                  Cancel
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default PropertyInventory;
